import { useState, useMemo } from 'react';
import { SearchIcon, XIcon } from 'lucide-react';
import type { Project } from '@mockd/shared';
import { ProjectCard } from './ProjectCard';
import { ProjectRow } from './ProjectRow';
import { EmptyState } from '../common/EmptyState';

interface ProjectListProps {
  projects: Project[];
  endpointCounts?: Record<string, number>;
  endpointLimit?: number;
  layout?: 'list' | 'grid';
}

export function ProjectList({ projects, endpointCounts = {}, endpointLimit, layout = 'list' }: ProjectListProps) {
  const [search, setSearch] = useState('');

  const filteredProjects = useMemo(() => {
    if (!search.trim()) return projects;
    const q = search.toLowerCase();
    return projects.filter(
      p => p.name.toLowerCase().includes(q) || p.subdomain.toLowerCase().includes(q)
    );
  }, [projects, search]);

  if (projects.length === 0) {
    return (
      <EmptyState
        title="No projects yet"
        description="Create your first project to start capturing and mocking HTTP requests."
      />
    );
  }

  return (
    <div>
      {projects.length > 3 && (
        <div className="mb-4">
          <div className="relative max-w-sm">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-base-content/70" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search projects..."
              className="input input-sm w-full pl-10"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/70 hover:text-base-content"
                aria-label="Clear search"
              >
                <XIcon className="size-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {filteredProjects.length === 0 ? (
        <div className="text-center py-8 text-base-content/70">
          <p>No projects matching &ldquo;{search}&rdquo;</p>
          <button onClick={() => setSearch('')} className="btn btn-ghost btn-sm mt-2">Clear search</button>
        </div>
      ) : layout === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProjects.map(project => (
            <ProjectCard
              key={project.id}
              project={project}
              endpointCount={endpointCounts[project.id]}
              endpointLimit={endpointLimit}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-box border border-base-300 overflow-hidden">
          {filteredProjects.map(project => (
            <ProjectRow
              key={project.id}
              project={project}
              endpointCount={endpointCounts[project.id]}
              endpointLimit={endpointLimit}
            />
          ))}
        </div>
      )}
    </div>
  );
}
